/**
 * ObsidianWeb — file tree component (Task 5).
 *
 * Renders the vault hierarchy in the left sidebar. Folders are collapsible;
 * files are click-to-open. Ordering matches Obsidian: directories first,
 * then files, both sorted case-insensitively (per FR-2.2).
 *
 * The component is **controlled** — expansion state lives in the parent
 * Shell so reopening a tab restores the same scroll/expansion view. The
 * pure helpers (`sortNodes`, `toggleExpanded`, `collectFiles`) are
 * exported so the smoke test can verify behaviour without a DOM.
 */

import type { TreeNode } from '../api.js';

export interface FileTreeProps {
  readonly nodes: readonly TreeNode[];
  readonly activePath: string | null;
  readonly expanded: ReadonlySet<string>;
  readonly onToggle: (dirPath: string) => void;
  readonly onPick: (filePath: string) => void;
}

/**
 * Sort a level of the tree: directories first (alpha, case-insensitive),
 * then files (alpha, case-insensitive). Stable for equal keys.
 */
export function sortNodes(nodes: readonly TreeNode[]): readonly TreeNode[] {
  const copy = [...nodes];
  copy.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'directory' ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
  return copy;
}

/** Immutable toggle: returns a new Set with `path` flipped. */
export function toggleExpanded(
  expanded: ReadonlySet<string>,
  path: string,
): ReadonlySet<string> {
  const next = new Set(expanded);
  if (next.has(path)) next.delete(path);
  else next.add(path);
  return next;
}

/** Recursively collect every file (not directory) under a node list. */
export function collectFiles(nodes: readonly TreeNode[]): readonly TreeNode[] {
  const out: TreeNode[] = [];
  const walk = (ns: readonly TreeNode[]): void => {
    for (const n of ns) {
      if (n.kind === 'file') out.push(n);
      else if (n.children) walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

/** SVG icon set — folder (open/closed) and file. Tiny inline to avoid a deps. */
function FolderIcon(props: { readonly open: boolean }): JSX.Element {
  return props.open ? (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M1.5 4h4l1.5 1.5h7A.5.5 0 0 1 14.5 6v.5H2.4L1 12.4V4.5A.5.5 0 0 1 1.5 4Z"
        fill="currentColor"
        opacity="0.65"
      />
      <path d="M2.4 7h12L13 13.5H1Z" fill="currentColor" />
    </svg>
  ) : (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M1.5 3h4l1.5 1.5h7A.5.5 0 0 1 14.5 5v7a.5.5 0 0 1-.5.5H1.5A.5.5 0 0 1 1 12V3.5a.5.5 0 0 1 .5-.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function FileIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M3 1.5A.5.5 0 0 1 3.5 1h6L13 4.5V14a.5.5 0 0 1-.5.5h-9A.5.5 0 0 1 3 14V1.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
      />
      <path d="M9.5 1v3.5H13" fill="none" stroke="currentColor" strokeWidth="1" />
    </svg>
  );
}

function TreeLevel(props: FileTreeProps): JSX.Element {
  const sorted = sortNodes(props.nodes);
  return (
    <ul className="ow-file-tree" role="group">
      {sorted.map((node) =>
        node.kind === 'directory' ? (
          <DirRow key={node.path} {...props} node={node} />
        ) : (
          <FileRow key={node.path} {...props} node={node} />
        ),
      )}
    </ul>
  );
}

function DirRow(props: FileTreeProps & { readonly node: TreeNode }): JSX.Element {
  const isOpen = props.expanded.has(props.node.path);
  const children = props.node.children ?? [];
  return (
    <li>
      <div
        className="ow-file-tree-row is-dir"
        role="treeitem"
        aria-expanded={isOpen}
        aria-label={`Folder ${props.node.name}`}
        tabIndex={0}
        onClick={() => props.onToggle(props.node.path)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            props.onToggle(props.node.path);
          }
        }}
        data-testid={`tree-dir-${props.node.path}`}
      >
        <span className="ow-file-tree-chevron" aria-hidden="true">
          {isOpen ? '▾' : '▸'}
        </span>
        <span className="ow-file-tree-icon">
          <FolderIcon open={isOpen} />
        </span>
        <span className="ow-file-tree-name" title={props.node.path}>
          {props.node.name}
        </span>
      </div>
      {isOpen ? (
        <TreeLevel
          nodes={children}
          activePath={props.activePath}
          expanded={props.expanded}
          onToggle={props.onToggle}
          onPick={props.onPick}
        />
      ) : null}
    </li>
  );
}

function FileRow(props: FileTreeProps & { readonly node: TreeNode }): JSX.Element {
  const isActive = props.activePath === props.node.path;
  return (
    <li>
      <div
        className={`ow-file-tree-row is-file${isActive ? ' active' : ''}`}
        role="treeitem"
        aria-selected={isActive}
        tabIndex={0}
        onClick={() => props.onPick(props.node.path)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            props.onPick(props.node.path);
          }
        }}
        data-testid={`tree-file-${props.node.path}`}
      >
        <span className="ow-file-tree-chevron" aria-hidden="true" />
        <span className="ow-file-tree-icon">
          <FileIcon />
        </span>
        <span className="ow-file-tree-name" title={props.node.path}>
          {props.node.name}
        </span>
      </div>
    </li>
  );
}

export function FileTree(props: FileTreeProps): JSX.Element {
  return <TreeLevel {...props} />;
}

export default FileTree;
